import type { OperationalEvidencePhoto } from '@trackeat/shared';
import type { CapturedPhoto } from '../adapters/camera/index.js';
import { flushLocalDb, openLocalDb } from '../db/local.js';
import { dbg } from '../debug/debugLog.js';
import { getSupabase } from '../supabase.js';

const BUCKET = 'operational-evidence';
const MAX_BYTES = 10_485_760;
let drainPromise: Promise<{ uploaded: number; failed: number }> | null = null;

export interface LocalOperationalEvidencePreview {
  targetPath: string;
  dataUrl: string;
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string;
}

function extensionForMime(mime: string): string {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}

function bytesFromBase64(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const digest = await crypto.subtle.digest('SHA-256', copy.buffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

async function requireFreshSession(): Promise<void> {
  const supabase = getSupabase();
  const current = await supabase.auth.getSession();
  if (current.error) throw current.error;
  let session = current.data.session;
  if (!session) throw new Error('Sessione scaduta: accedi di nuovo per caricare la foto');
  if (session.expires_at != null && session.expires_at * 1000 <= Date.now() + 60_000) {
    const refreshed = await supabase.auth.refreshSession();
    if (refreshed.error) throw refreshed.error;
    session = refreshed.data.session;
    if (!session) throw new Error('Sessione scaduta: accedi di nuovo per caricare la foto');
  }
}

function boundedError(error: unknown): string {
  if (error instanceof Error) return error.message.slice(0, 500);
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message.slice(0, 500);
  }
  return String(error).slice(0, 500);
}

function isAlreadyUploaded(error: unknown): boolean {
  const message = boundedError(error).toLowerCase();
  return message.includes('already exists') || message.includes('duplicate');
}

export async function stageOperationalEvidence(input: {
  tenantId: string;
  businessId: string;
  kind: 'cleaning' | 'temperature' | 'register';
  targetId: string;
  eventClientUuid: string;
  photo: CapturedPhoto;
  capturedAt?: string;
}): Promise<OperationalEvidencePhoto> {
  const mime = input.photo.mime === 'image/jpg' ? 'image/jpeg' : input.photo.mime.toLowerCase();
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(mime)) {
    throw new Error('Formato foto non supportato');
  }
  const bytes = bytesFromBase64(input.photo.base64);
  if (bytes.byteLength < 1 || bytes.byteLength > MAX_BYTES) {
    throw new Error('La foto deve pesare meno di 10 MB');
  }
  const capturedAt = input.capturedAt ?? new Date().toISOString();
  const sha256 = await sha256Hex(bytes);
  const path = [
    input.tenantId,
    input.businessId,
    input.kind,
    input.targetId,
    `${input.eventClientUuid}.${extensionForMime(mime)}`,
  ].join('/');
  const now = new Date().toISOString();
  const db = await openLocalDb(null);
  await db.run(
    `INSERT INTO pending_operational_evidence_v1(
       event_client_uuid,tenant_id,business_id,evidence_kind,target_id,target_path,
       mime,base64,sha256,size_bytes,next_attempt_at,queued_at,updated_at
     ) VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?) ON CONFLICT(event_client_uuid) DO NOTHING`,
    [
      input.eventClientUuid, input.tenantId, input.businessId, input.kind,
      input.targetId, path, mime, input.photo.base64, sha256, bytes.byteLength,
      now, now, now,
    ],
  );
  const persisted = await db.query('SELECT sha256,target_path FROM pending_operational_evidence_v1 WHERE event_client_uuid = ?', [input.eventClientUuid]);
  if (persisted.values?.[0]?.sha256 !== sha256 || persisted.values?.[0]?.target_path !== path) throw new Error('La foto salvata non corrisponde alla bozza');
  await flushLocalDb({ required: true });
  return { storage_path: path, sha256, mime: mime as OperationalEvidencePhoto['mime'], size_bytes: bytes.byteLength, captured_at: capturedAt };
}

export async function discardOperationalEvidence(eventClientUuid: string): Promise<void> {
  const db = await openLocalDb(null);
  await db.run('DELETE FROM pending_operational_evidence_v1 WHERE event_client_uuid = ?', [eventClientUuid]);
  await flushLocalDb();
}

export async function purgeOperationalEvidenceOutsideTenants(validTenantIds: readonly string[]): Promise<number> {
  const db = await openLocalDb(null);
  const where = validTenantIds.length > 0
    ? `tenant_id NOT IN (${validTenantIds.map(() => '?').join(',')})`
    : '1 = 1';
  const result = await db.run(`DELETE FROM pending_operational_evidence_v1 WHERE ${where}`, [...validTenantIds]);
  await flushLocalDb();
  return Number((result as { changes?: { changes?: number } }).changes?.changes ?? 0);
}

export async function listLocalOperationalEvidencePreviews(
  tenantId: string,
  targetPaths: readonly string[],
): Promise<Record<string, LocalOperationalEvidencePreview>> {
  if (targetPaths.length === 0) return {};
  const db = await openLocalDb(null);
  const uniquePaths = [...new Set(targetPaths)].slice(0, 100);
  const result = await db.query(
    `SELECT target_path,mime,base64,attempts,last_error,next_attempt_at
       FROM pending_operational_evidence_v1
      WHERE tenant_id = ? AND target_path IN (${uniquePaths.map(() => '?').join(',')})`,
    [tenantId, ...uniquePaths],
  );
  const previews: Record<string, LocalOperationalEvidencePreview> = {};
  for (const raw of result.values ?? []) {
    const row = raw as {
      target_path: string; mime: string; base64: string; attempts: number;
      last_error: string | null; next_attempt_at: string;
    };
    previews[row.target_path] = {
      targetPath: row.target_path,
      dataUrl: `data:${row.mime};base64,${row.base64}`,
      attempts: Number(row.attempts),
      lastError: row.last_error,
      nextAttemptAt: row.next_attempt_at,
    };
  }
  return previews;
}

export async function makeOperationalEvidenceRetryable(
  tenantId: string,
  targetPaths: readonly string[],
): Promise<void> {
  if (targetPaths.length === 0) return;
  const db = await openLocalDb(null);
  const uniquePaths = [...new Set(targetPaths)].slice(0, 100);
  const now = new Date().toISOString();
  await db.run(
    `UPDATE pending_operational_evidence_v1
        SET next_attempt_at = ?, updated_at = ?
      WHERE tenant_id = ? AND target_path IN (${uniquePaths.map(() => '?').join(',')})`,
    [now, now, tenantId, ...uniquePaths],
  );
  await flushLocalDb();
}

export async function getOperationalEvidenceUploadState(
  tenantId: string,
  eventClientUuid: string,
): Promise<{ pending: boolean; lastError: string | null }> {
  const db = await openLocalDb(null);
  const result = await db.query(
    `SELECT last_error FROM pending_operational_evidence_v1
      WHERE tenant_id = ? AND event_client_uuid = ? LIMIT 1`,
    [tenantId, eventClientUuid],
  );
  const row = result.values?.[0] as { last_error?: string | null } | undefined;
  return { pending: !!row, lastError: row?.last_error ?? null };
}

export async function drainOperationalEvidenceOutbox(
  tenantId: string,
  limit = 3,
  eventClientUuid?: string,
): Promise<{ uploaded: number; failed: number }> {
  if (navigator.onLine === false) return { uploaded: 0, failed: 0 };
  if (drainPromise) return drainPromise;
  drainPromise = (async () => {
    const db = await openLocalDb(null);
    const now = new Date();
    const eventFilter = eventClientUuid ? ' AND event_client_uuid = ?' : '';
    const rows = await db.query(
      `SELECT event_client_uuid,target_path,mime,base64,sha256,size_bytes,attempts
         FROM pending_operational_evidence_v1
        WHERE tenant_id = ?${eventFilter} AND next_attempt_at <= ?
        ORDER BY next_attempt_at,queued_at LIMIT ?`,
      eventClientUuid
        ? [tenantId, eventClientUuid, now.toISOString(), Math.max(1, Math.min(limit, 10))]
        : [tenantId, now.toISOString(), Math.max(1, Math.min(limit, 10))],
    );
    let uploaded = 0;
    let failed = 0;
    for (const raw of rows.values ?? []) {
      const row = raw as {
        event_client_uuid: string; target_path: string; mime: string; base64: string;
        sha256: string; size_bytes: number; attempts: number;
      };
      try {
        await requireFreshSession();
        const bytes = bytesFromBase64(row.base64);
        if (bytes.byteLength !== Number(row.size_bytes) || await sha256Hex(bytes) !== row.sha256) {
          throw new Error('Foto locale danneggiata: serve una nuova acquisizione');
        }
        const storage = getSupabase().storage.from(BUCKET);
        const upload = await storage.upload(row.target_path, bytes, { contentType: row.mime, upsert: false });
        if (upload.error && !isAlreadyUploaded(upload.error)) throw upload.error;
        if (upload.error) {
          const remote = await storage.download(row.target_path);
          if (remote.error || !remote.data || await sha256Hex(new Uint8Array(await remote.data.arrayBuffer())) !== row.sha256) {
            throw new Error('La foto remota esistente non coincide con quella acquisita');
          }
        }
        await db.run('DELETE FROM pending_operational_evidence_v1 WHERE event_client_uuid = ?', [row.event_client_uuid]);
        uploaded += 1;
      } catch (error) {
        const attempts = Number(row.attempts) + 1;
        const retryAt = new Date(Date.now() + Math.min(300_000, 5_000 * 2 ** Math.min(attempts, 6))).toISOString();
        await db.run(
          `UPDATE pending_operational_evidence_v1
              SET attempts = ?, last_error = ?, next_attempt_at = ?, updated_at = ?
            WHERE event_client_uuid = ?`,
          [attempts, boundedError(error), retryAt, new Date().toISOString(), row.event_client_uuid],
        );
        dbg.warn('operational-evidence', 'upload_failed', {
          event_client_uuid: row.event_client_uuid,
          tenant_id: tenantId,
          attempts,
          error: boundedError(error),
        });
        failed += 1;
      }
    }
    await flushLocalDb();
    return { uploaded, failed };
  })();
  try { return await drainPromise; } finally { drainPromise = null; }
}
